import React, { CSSProperties } from "react";
import type { TabContentProps, TabContentState } from "./types";
import { translations } from "./translations"
import CTAButton from "src/components/ctabutton/ctabutton";
import { DatePickerV2 } from "src/components/datePicker";
import GuestPicker from "src/components/guestPicker.v2/guestPickerConfigurable";
import Icon from "src/components/icon";
import type { InputSlot } from "src/modules/bookingWidget/types";
import { getLanguageField } from "src/utils/language";
import DateTime from "src/utils/temporalLuxonUtil";
import QuantitySelector from "src/components/quantitySelector";
import { fromDDMMYYYY, toDDMMYYYY, toYYYYMMDD } from "src/utils/datefns";
import { getSlug } from "src/utils/slugs";
import DropdownRoomPicker from "src/@components/dropdown.room.picker/dropdown.room.picker.component";

/**
 * TODO:
 * Add config to add currency to QS (in input next to multi_room)
 */

export default class TabContent extends React.Component<TabContentProps, TabContentState> {

  state: TabContentState = {
    rooms: this.defaultRooms(),
    link: "",
    days: 1,
    startDate: this.defaultStartDate(),
    endDate: this.defaultEndDate(),
    adults: 1,
    children: 0,
    ages: undefined,
    selectedOption: 0
  }

  get startDate() {
    const date = this.state.startDate;
    if (!date) return ""

    const slot = this.getDateRangePickerSlot() || this.getDatePickerSlot();
    if (slot && slot.dates_format === "DD/MM/YYYY") return toDDMMYYYY(date)
    return toYYYYMMDD(date);
  }

  get endDate() {
    const date = this.state.endDate;
    if (!date) return "";

    const slot = this.getDateRangePickerSlot() || this.getDatePickerSlot();
    if (slot && slot.dates_format === "DD/MM/YYYY") return toDDMMYYYY(date)
    return toYYYYMMDD(date);
  }

  get numberOfDays() {
    if (!this.state.endDate || !this.state.startDate) return ""
    return ((this.state.endDate.getTime() - this.state.startDate.getTime()) / 86400000).toString();
  }

  get link() {
    const dropdown_slot = this.getDropdownSlot();
    if (dropdown_slot && dropdown_slot.dropdown_options && dropdown_slot.dropdown_options.length) {
      const selected_option = dropdown_slot.dropdown_options[this.state.selectedOption].link_id;
      if (selected_option.link_type === "internal") return getSlug(this.props.language, this.props.default_language, selected_option.linked_page?.translations || [], "slug")
      return getLanguageField(dropdown_slot.dropdown_options[this.state.selectedOption].link_id?.link_translations, this.props.language, "link") as string;
    }
    if (this.props.tab_slot.link === "internal") return getSlug(this.props.language, this.props.default_language, this.props.tab_slot.linked_page?.translations, "slug") as string;
    else return getLanguageField(this.props.tab_slot?.link_translations || [], this.props.language, "link") as string;
  }

  get multi_room_guests() {
    if (!this.state.rooms) return {
      adults: '',
      childages: ''
    }

    let rooms_adults: number[] = [];
    let rooms_childs: string[] = [];

    for (const room of this.state.rooms) {
      let adults = 0;
      let childs = [];
      for (const guest of room) {
        if (guest.type === 'adult') adults++;
        else if (guest.type === 'junior') childs.push(guest.age);
      }
      rooms_adults.push(adults);
      rooms_childs.push(childs.join(","));
    }
    return { adults: rooms_adults.join("-"), childages: rooms_childs.join("-") }
  }



  get dropdown_QS_overrides() {
    const dropdown_slot = this.getDropdownSlot();
    if (dropdown_slot && dropdown_slot.dropdown_options && (dropdown_slot.dropdown_options.length >= this.state.selectedOption)) {
      const override_in_selected_option = dropdown_slot.dropdown_options[this.state.selectedOption].link_id.querystring_overrides;
      if (override_in_selected_option) return override_in_selected_option
      else return dropdown_slot.querystring_overrides;
    }

    return []
  }

  get search_form_configuration() {
    return this.props.tab_slot.search_form_configuration;
  }

  get linkWithQs() {
    let link = `${this.link}`;
    const dropdown_slot = this.getDropdownSlot();
    const datepicker_slot = this.getDatePickerSlot();
    const daterangepicker_slot = this.getDateRangePickerSlot();
    const counter_slot = this.getCounterSlot();
    const guestpicker_slot = this.getGuestPickerSlot();
    const dropdown_overrides = this.dropdown_QS_overrides

    if (datepicker_slot) {
      let startdate_dropdown_override = null
      if (dropdown_slot) dropdown_overrides?.find(qso => qso.from === "startdate")
      const datepicker_override = startdate_dropdown_override || datepicker_slot.querystring_overrides?.find(qso => qso.from === "startdate")
      if (datepicker_override) link = this.addQS(link, datepicker_override.to, this.startDate)
      else link = this.addQS(link, "startdate", toYYYYMMDD(fromDDMMYYYY(this.startDate)))
    }
    if (daterangepicker_slot) {
      let startdate_dropdown_override = null
      let enddate_dropdown_override = null
      // check for override in dropdown
      if (dropdown_slot) {
        startdate_dropdown_override = dropdown_overrides?.find(qso => qso.from === "startdate")
        enddate_dropdown_override = dropdown_overrides?.find(qso => qso.from === "enddate")
      }
      const daterangepicker_start_override = startdate_dropdown_override || daterangepicker_slot.querystring_overrides?.find(qso => qso.from === "startdate")
      const daterangepicker_end_override = enddate_dropdown_override ||
        (daterangepicker_slot.date_range_picker_type === "counter"
          ? daterangepicker_slot.querystring_overrides?.find(qso => qso.from === "days") //override for type counter
          : daterangepicker_slot.querystring_overrides?.find(qso => qso.from === "enddate"))
      const end_date = daterangepicker_slot.date_range_picker_type === "counter" ? this.numberOfDays : this.endDate;

      if (daterangepicker_start_override) link = this.addQS(link, daterangepicker_start_override.to, this.startDate)
      else link = this.addQS(link, "startdate", this.startDate)
      if (daterangepicker_end_override) {
        link = this.addQS(link, daterangepicker_end_override.to, end_date)
      }
      else link = this.addQS(link, "enddate", end_date)
    }
    if (guestpicker_slot) {
      let adults_dropdown_override = null
      let childs_dropdown_override = null
      if (dropdown_slot) {
        adults_dropdown_override = dropdown_overrides?.find(qso => qso.from === "adult")
        childs_dropdown_override = dropdown_overrides?.find(qso => qso.from === "childs")
      }
      const guestpicker_adults_override = adults_dropdown_override || guestpicker_slot.querystring_overrides?.find(qso => qso.from === "adult")
      const guestpicker_children_override = childs_dropdown_override || guestpicker_slot.querystring_overrides?.find(qso => qso.from === "childs")
      const guestpicker_childages_override = childs_dropdown_override || guestpicker_slot.querystring_overrides?.find(qso => qso.from === "childages")

      const guest_picker_same_override = guestpicker_adults_override?.to === guestpicker_children_override?.to
      if (guest_picker_same_override && guestpicker_adults_override && guestpicker_children_override) {
        link = this.addQS(link, guestpicker_adults_override.to, ((this.state.adults || 0) + (this.state.children || 0)).toString())
      }
      else {
        const { multi_room } = guestpicker_slot

        if (multi_room) {
          const adults = this.multi_room_guests.adults
          const childages = this.multi_room_guests.childages
          if (guestpicker_adults_override) link = this.addQS(link, guestpicker_adults_override.to, adults)
          else link = this.addQS(link, "adult", adults)
          if (guestpicker_childages_override) link = this.addQS(link, guestpicker_childages_override.to, childages)
          else link = this.addQS(link, "childages", childages)

          if (this.state.rooms) {
            link = this.addQS(link, "numberOfRooms", this.state.rooms.length.toString())
            link = this.addQS(link, "currentRoom", "1")
          }
        }
        else {
          const adults = this.state.adults.toString();
          const children = this.state.children.toString();
          if (guestpicker_adults_override) link = this.addQS(link, guestpicker_adults_override.to, adults)
          else link = this.addQS(link, "adult", adults)
          if (guestpicker_children_override) link = this.addQS(link, guestpicker_children_override.to, children)
          else link = this.addQS(link, "childs", children)
        }
      }
    }
    if (counter_slot) {
      const counter_slot_override = counter_slot.querystring_overrides?.find(qso => qso.from === "days")
      if (counter_slot_override) link = this.addQS(link, counter_slot_override.to, this.state.days.toString())
      else link = this.addQS(link, "days", this.state.days.toString())
    }
    return link;
  }

  get style() {
    return {
      '--element-border-color': this.props.element_border_color,
      '--element-background-color': this.props.element_background_color,
      '--element-text-color': this.props.element_text_color,
      '--element-placeholder-text-color': this.props.element_placeholder_text_color,
      '--bg-color': this.props.bg_color,
    } as CSSProperties
  }

  get blackout_periods() {
    const { search_form_configuration } = this.props.tab_slot
    const blackoutPeriods = search_form_configuration?.blackout_periods || [];

    return (blackoutPeriods && blackoutPeriods.length && blackoutPeriods.map(bp => {
      const start = new Date(new Date(bp.From || new Date()).setHours(0, 0, 0, 0))
      const end = new Date(new Date(bp.To || new Date()).setHours(0, 0, 0, 0))
      return { start, end }
    })) || []
  }

  setStartDate = (date: Date) => {
    if (!date) return;
    const ts = date.getTime();
    if (this.state.endDate) {
      const ets = this.state.endDate.getTime();
      if (ts > ets) {
        const plus1 = DateTime.fromJSDate(date).plus({ day: 1 }).toJSDate()
        this.setState({ startDate: date, endDate: plus1 });
      }
    }
    this.setState({ startDate: date });
  }

  setEndDate = (date: Date) => this.setState({ endDate: date })
  setAdults = (adults: number) => this.setState({ adults })
  setChilds = (childs: number, ages: number[]) => this.setState({ children: childs, ages })
  onOptionChange = (value: number) => this.setState({ selectedOption: value })
  onDaysChange = (days: number) => this.setState({ days })
  setRooms = (rooms: Array<GuestSelectorValue[]>) => this.setState({ rooms })

  defaultStartDate(): Date {
    const datepicker = this.getDatePickerSlot();
    const daterangepicker = this.getDateRangePickerSlot()
    if (datepicker && datepicker.default_search_date) return new Date(datepicker.default_search_date);
    if (daterangepicker && daterangepicker.default_start_search_date) return new Date(daterangepicker.default_start_search_date);
    return this.minimumDate();
  }

  minimumDate(): Date {
    const baseDate = new Date()
    const hour = baseDate.getHours();
    const today = DateTime.fromJSDate(baseDate).startOf("day").toJSDate();
    const tomorrow = DateTime.fromJSDate(baseDate).startOf("day").plus({ day: 1 }).startOf("day").toJSDate();
    const minimumStart = hour >= 15 ? tomorrow : today;
    return minimumStart;
  }

  defaultRooms() {
    const slot = this.getGuestPickerSlot()
    if (!slot) return undefined;
    return DropdownRoomPicker.getDefaultValue((this.search_form_configuration?.age_categories || []).map(({ age_category_id }) => age_category_id) || [], { "adult": 1 })
  }


  dateIntervals() {
    const min_date_interval = this.minimumDate();
    const max_date_interval = this.maximumDate();

    return [{
      start: min_date_interval as Date,
      end: max_date_interval
    }]

  }

  defaultEndDate(): Date {
    const slot = this.getDateRangePickerSlot();
    if (slot && slot.default_end_search_date) return new Date(slot.default_end_search_date);
    if (slot) {
      const baseDate = this.minimumDate();
      const days_to_add = this.search_form_configuration?.min_stay || 0
      const date = DateTime.fromJSDate(baseDate).startOf("day").plus({ days: days_to_add }).startOf("day").toJSDate();
      return date;
    }
    return this.minimumDate();
  }

  maximumDate(): Date {
    const baseDate = this.minimumDate();
    const nextYears = DateTime.fromJSDate(baseDate).startOf("day").plus({ year: 2 }).startOf("day").toJSDate();
    return nextYears;
  }

  getDropdownSlot() {
    const { tab_slot } = this.props;
    const dropdownInFirstSlot = tab_slot.input_slot_1?.type === "dropdown" && tab_slot.input_slot_1
    const dropdownInSecondSlot = tab_slot.input_slot_2?.type === "dropdown" && tab_slot.input_slot_2
    const dropdownInThirdSlot = tab_slot.input_slot_3?.type === "dropdown" && tab_slot.input_slot_3

    return dropdownInFirstSlot || dropdownInSecondSlot || dropdownInThirdSlot;
  }

  getDatePickerSlot() {
    const { tab_slot } = this.props;
    const datepickerInFirstSlot = tab_slot.input_slot_1?.type === "datepicker" && tab_slot.input_slot_1
    const datepickerInSecondSlot = tab_slot.input_slot_2?.type === "datepicker" && tab_slot.input_slot_2
    const datepickerInThirdSlot = tab_slot.input_slot_3?.type === "datepicker" && tab_slot.input_slot_3

    return datepickerInFirstSlot || datepickerInSecondSlot || datepickerInThirdSlot;
  }

  getDateRangePickerSlot() {
    const { tab_slot } = this.props;
    const daterangepickerInFirstSlot = tab_slot.input_slot_1?.type === "daterangepicker" && tab_slot.input_slot_1
    const daterangepickerInSecondSlot = tab_slot.input_slot_2?.type === "daterangepicker" && tab_slot.input_slot_2
    const daterangepickerInThirdSlot = tab_slot.input_slot_3?.type === "daterangepicker" && tab_slot.input_slot_3

    return daterangepickerInFirstSlot || daterangepickerInSecondSlot || daterangepickerInThirdSlot;
  }

  getCounterSlot() {
    const { tab_slot } = this.props;
    const counterInFirstSlot = tab_slot.input_slot_1?.type === "counter" && tab_slot.input_slot_1
    const counterInSecondSlot = tab_slot.input_slot_2?.type === "counter" && tab_slot.input_slot_2
    const counterInThirdSlot = tab_slot.input_slot_3?.type === "counter" && tab_slot.input_slot_3

    return counterInFirstSlot || counterInSecondSlot || counterInThirdSlot;
  }

  getGuestPickerSlot() {
    const { tab_slot } = this.props;
    const guestpickerInFirstSlot = tab_slot.input_slot_1?.type === "guestpicker" && tab_slot.input_slot_1
    const guestpickerInSecondSlot = tab_slot.input_slot_2?.type === "guestpicker" && tab_slot.input_slot_2
    const guestpickerInThirdSlot = tab_slot.input_slot_3?.type === "guestpicker" && tab_slot.input_slot_3

    return guestpickerInFirstSlot || guestpickerInSecondSlot || guestpickerInThirdSlot;
  }

  addQS(url: string, qs: string, value: string) {
    if (!qs || !value || !url) return url;
    const hasQS = url.indexOf("?") !== -1
    if (hasQS) return `${url}&${qs}=${value}`
    return `${url}/?${qs}=${value}`
  }

  isDayAvailable(date: Date, min_days: number = 0, max_days: number = 0) {
    if (this.state.endDate) return true;
    if (!this.state.startDate) return true;
    const one_day_ms = 86400000
    const min_ms = (min_days - 1) * one_day_ms;
    const max_ms = (max_days) * one_day_ms;
    const et = date.getTime();
    const st = this.state.startDate.getTime();
    const diff = et - st;
    if (diff < 0) return false;
    const day_ms = (date.getTime() - this.state.startDate.getTime())
    return (day_ms > min_ms) && day_ms < max_ms;
  }

  renderSlot(slot: InputSlot) {
    if (slot.type === "dropdown") return this.renderDropdown(slot);
    if (slot.type === "counter") return this.renderCounter(slot);
    if (slot.type === "datepicker") return this.renderDatePicker();
    if (slot.type === "daterangepicker") return this.renderDateRangePicker(slot);
    if (slot.type === "guestpicker") return this.renderGuestPicker(slot);
    return null;
  }

  renderDropdown(slot: InputSlot) {
    if (!slot.dropdown_options) return null;
    return <select
      value={this.state.selectedOption}
      onChange={(e) => this.onOptionChange(parseInt(e.target.value))}
      className="w-full rounded-lg bg-[color:var(--element-background-color)] border-[color:var(--element-border-color)] text-[color:var(--element-text-color)] global-bselect caption h-10"
    >
      {(slot.dropdown_options || []).map((o, idx) => <option value={idx} key={idx}>
        {getLanguageField(o.link_id.translations, this.props.language, "label")}
      </option>)}
    </select>
  }

  renderGuestPicker(slot: InputSlot) {
    const { multi_room } = slot;
    const parsed_age_categories = (this.search_form_configuration?.age_categories || []).map(({ age_category_id }) => age_category_id) || []

    if (multi_room) return <DropdownRoomPicker
      rooms={this.state.rooms}
      onChange={this.setRooms}
      ageCategories={parsed_age_categories}
      inputColor="var(--element-text-color)"
      backgroundColor="var(--element-background-color)"
      borderColor="var(--element-border-color)"
    />

    return <GuestPicker
      guests={parsed_age_categories}
      language={this.props.language}
      adults={this.state.adults}
      children={this.state.children}
      ages={this.state.ages}
      setAdults={this.setAdults}
      setChilds={this.setChilds}
      backgroundColor="var(--element-background-color)"
      borderColor="var(--element-border-color)"
      inputColor="var(--element-text-color)"
    />
  }

  renderDateRangePicker(input_slot: InputSlot) {
    const weekdays_blocked = (this.search_form_configuration?.blocked_days || []).map(day => parseInt(day))
    return <React.Suspense fallback={<div className="w-full h-10" />}>
      <DatePickerV2
        excludeDateIntervals={this.blackout_periods}
        isRange={true}
        onChange={(dates: Date[]) => { this.setStartDate(dates[0]); this.setEndDate(dates[1]); }}
        startDate={this.state.startDate}
        isDayBlocked={(date) => this.isDayAvailable(date, this.search_form_configuration?.min_stay || 0, this.search_form_configuration?.max_stay || 99)}
        label={translations[this.props.language as Language].arrival}
        endLabel={translations[this.props.language as Language].departure}
        endDate={this.state.endDate}
        weekdaysBlocked={weekdays_blocked}
        openToDate={this.state.startDate}
        removeMinDate
        includeDateIntervals={this.dateIntervals()}
        inputBackgroundColor="var(--element-background-color)"
        inputPlaceholderColor="var(--element-placeholder-text-color)"
        inputTextColor="var(--element-text-color)"
        inputBorderColor="var(--element-border-color)"
        selectedDayBgColor=""
      />
    </React.Suspense>
  }

  renderDatePicker() {
    return <DatePickerV2
      excludeDateIntervals={this.blackout_periods}
      isRange={false}
      onChange={(date: Date) => { this.setStartDate(date); }}
      value={this.state.startDate}
      label={translations[this.props.language as Language].arrival}
      endLabel={translations[this.props.language as Language].departure}
      openToDate={this.state.startDate}
      includeDateIntervals={this.dateIntervals()}
      inputBackgroundColor="var(--element-background-color)"
      inputPlaceholderColor="var(--element-placeholder-text-color)"
      inputTextColor="var(--element-text-color)"
      inputBorderColor="var(--element-border-color)"
      selectedDayBgColor=""
    />
  }

  renderCounter(slot: InputSlot) {
    const label = getLanguageField(slot.counter_translations, this.props.language, "label")
    return <div className="flex flex-row gap-3 items-center justify-around">
      <span className="boldFontFamily searchWidgetText text-[color:var(--element-text-color)]">{label}</span>
      <QuantitySelector
        textColor="var(--element-text-color)"
        borderColor="var(--element-border-color)"
        quantity={this.state.days}
        onPlusClick={() => this.onDaysChange(this.state.days + 1)}
        onMinusClick={() => this.onDaysChange(this.state.days - 1)}
        disabledOpacity="opacity-50"
        min={1}
        max={12}
      />
    </div>
  }

  render() {
    return <>
      <div className={`flex flex-col lg:flex-row gap-4 items-center p-4 sm:p-6 rounded-b-2xl`} style={this.style}>
        {this.props.tab_slot.input_slot_1 &&
          <div className="w-full lg:w-[calc(25%_-_16px)]">
            {this.renderSlot(this.props.tab_slot.input_slot_1)}
          </div>
        }
        {this.props.tab_slot.input_slot_2 &&
          <div className="w-full lg:w-[calc(25%_-_16px)]">
            {this.renderSlot(this.props.tab_slot.input_slot_2)}
          </div>
        }
        {this.props.tab_slot.input_slot_3 &&
          <div className="w-full lg:w-[calc(25%_-_16px)]">
            {this.renderSlot(this.props.tab_slot.input_slot_3)}
          </div>
        }
        <div className="w-full min-w-[25%] lg:w-1/4 ml-auto">
          <CTAButton
            link={this.linkWithQs}
            className="w-full"
            color={this.props.cta_button_label_color}
            bgcolor={this.props.cta_button_bg_color}
            hovercolor={this.props.cta_button_hover_label_color}
            hoverbgcolor={this.props.cta_button_hover_bg_color}
            type="primary"
            text={getLanguageField(this.props.tab_slot.translations, this.props.language, "button_text") as string}
            icon={<Icon className="w-6 h-4" icon="ArrowRight" />}
          />
        </div>
      </div>
    </>
  }
}